import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  getProfile,
  getOrganizationProducts,
  getEnrichedOrganization,
  generateHgJwt,
  generateMbJwt,
  setMbHomepage,
  getSession,
  subscribeToAuthStateChange,
} from '../libs/fetch';
import { getEnrichedIntegrations } from '../data/integrations';
import posthog from 'posthog-js';
import { boot, shutdown } from '@intercom/messenger-js-sdk';
import DH_Logo from '../assets/dirty_hands/DH_Logo_Classic_Grn+Gry.png';

const AuthContext = createContext();
export const useAuthContext = () => useContext(AuthContext);

// Top of file
let _loadProfile = null;

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(null);
  const [updatingHomepage, setUpdatingHomepage] = useState(false);
  const [currentMetabaseUrl, setCurrentMetabaseUrl] = useState('');
  const [currentAnalyticsHomepage, setCurrentAnalyticsHomepage] = useState(null);
  const [integrations, setIntegrations] = useState(null);

  const user = useMemo(() => session?.user ?? null, [session]);

  useEffect(() => {
    const initSession = async () => {
      try {
        const {
          session: s,
          error,
        } = await getSession();
        if (error) throw error;
        if (s) setSession(s); // triggers profile loader
        else setLoading(false); // no session, not loading
      } catch (err) {
        console.error('❌ Failed to initialize session:', err);
        setLoading(false);
      }
    };

    initSession();

    const subscription = subscribeToAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setProfile(null);
        setCurrentOrganization(null);
        setOrganization(null);
        shutdown();
        posthog.reset();
      } else {
        if (!newSession?.access_token) return;
        // ✅ Don't update if token hasn't changed
        setSession((prev) => {
          if (newSession.access_token === prev?.access_token) return prev;
          return newSession;
        });
      }
    });

    return () => subscription?.unsubscribe();
  }, []); // ✅ Only run once on mount

  useEffect(() => {
    _loadProfile = async () => {
      if (!session?.user) {
        setLoading(false);
        return;
      }

      try {
        posthog.identify(session.user.id, {
          name: session.user.user_metadata?.first_name,
          email: session.user.identities?.[0]?.email,
        });

        const profileData = await getProfile();
        setProfile(profileData);
        const allowedOrgIds = profileData.allowed_organizations.map(o => o.id);
        const primaryOrgId = profileData.primary_organization_id;
        const defaultOrgId = profileData.default_organization_id || primaryOrgId;
        setCurrentAnalyticsHomepage(profileData.analytics_homepage ?? '/');

        setCurrentOrganization(prev => {
          if (!prev || !allowedOrgIds.includes(prev)) {
            return defaultOrgId;
          }
          return prev;
        });

        const primaryCompany = profileData.allowed_organizations.find(
          (o) => o.id === primaryOrgId
        )?.name;

        boot({
          app_id: 'sqmzduox',
          custom_launcher_selector: '#support',
          hide_default_launcher: true,
          name: profileData.full_name,
          created_at: session.user.created_at,
          email: session.user.email,
          user_hash: profileData.intercom_hash,
          user_id: profileData.id,
          company: {
            company_id: primaryOrgId,
            name: primaryCompany,
          },
        });
      } catch (err) {
        console.error('❌ Error loading profile or org:', err);
        setProfile(null);
        setCurrentOrganization(null);
      } finally {
        setLoading(false);
      }
    };
    if (session) _loadProfile();
  }, [session]);

  useEffect(() => {
    if (currentOrganization) {
      //Enrich Org
      (async () => {
        try {
          setLoading(true);
          const { data, error } = await getEnrichedOrganization(currentOrganization);
          if (error) throw error;
          setOrganization(data);
        } catch (err) {
          console.error('❌ Error fetching organization:', err);
        } finally {
          setLoading(false);
        }
      })(); 

      //Set Products
      (async () => {
        try {
          const prods = await getOrganizationProducts(
            currentOrganization
          );
          setProducts(prods);
        } catch (err) {
          console.error('❌ Error fetching products:', err);
        }
      })();

      //Set Integrations
      (async () => {
        try {
          const integrations = await getEnrichedIntegrations(currentOrganization);
          setIntegrations(integrations);
        } catch (err) {
          console.error('❌ Error fetching integrations:', err);
        }
      })();
    }
  }, [currentOrganization]);

  const getHgJwt = async () => {
    if (!currentOrganization) return null;
    try {
      const { data, error } = await generateHgJwt();
      if (error) console.error('❌ Error generating HG JWT:', error);
      return data;
    } catch (err) {
      console.error('❌ HG JWT exception:', err);
      return null;
    }
  };

  const getMbJwt = async () => {
    if (!currentOrganization) return null;
    try {
      const { data, error } = await generateMbJwt(currentOrganization);
      if (error) console.error('❌ Error generating MB JWT:', error);
      return data;
    } catch (err) {
      console.error('❌ MB JWT exception:', err);
      return null;
    }
  };

  const allowedOrganizations = useMemo(
    () => profile?.allowed_organizations ?? [],
    [profile]
  );
  const currentOrg = useMemo(
    () => allowedOrganizations.find((org) => org.id === currentOrganization),
    [allowedOrganizations, currentOrganization]
  );
  
  // Helper function to check if user can edit products
  const canEditProducts = useMemo(() => {
    return organization?.permissions?.products == 'edit'
  }, [organization]);

  const isMuffinAdmin = useMemo(() => {
    return profile?.primary_organization_id === '63a34992-d053-4349-b1d6-6d67a985ba79';
  }, [profile]);
  
  const forceSetPassword = profile?.requires_password_set || false;

  const allowedNavigation = useMemo(() => {
    let nav = [];
    const addNavigationItem = (item) => {
      if (!nav.some((navItem) => navItem.id === item.id)) {
        nav.push(item);
      }
    };

    if (!forceSetPassword) {
      if (
        organization?.permissions?.analytics
      ) {
        addNavigationItem({
          name: 'Analytics',
          href: '/analytics',
          id: 'fe__analytics__view',
        });
      }
      if (organization?.available_embeds?.length > 0) {

        const parents = new Set(organization.available_embeds.map((embed) => embed.parent));
        parents.forEach((parent) => {
          const parentNav = {
            name: parent,
            id: `embed__${parent}__view`,
            subNavigation: [],
          }
          const children = organization.available_embeds.filter((embed) => embed.parent === parent);
          children.forEach((child) => {
            const childNav = {
              name: child.name,
              description: child.description,
              href: `/analytics/${child.id}`,
              id: `embed__${child.id}__view`,
            }
            if (parent === 'Dirty Hands') {
              childNav.image = DH_Logo;
            }
            parentNav.subNavigation.push(childNav);
          });
          parentNav.href = parentNav?.subNavigation?.[0]?.href;
          addNavigationItem(parentNav);
        });
      }
      if (organization?.permissions?.integrations) {
        const invalidIntegrations = integrations?.available?.filter((i) => i.credential_status === "invalid" && i.connection_status === "active").length;
        const unmappedProductsIntegrations = integrations?.available?.filter((i) => i.unmapped_products > 0).length;
        addNavigationItem({
          name: 'Integrations',
          href: '/integrations',
          id: 'fe__integrations__view',
          icon: invalidIntegrations > 0 ? 'error' : unmappedProductsIntegrations > 0 ? 'warning' : null,
        });
      }
      if (organization?.permissions?.products) {
        addNavigationItem({
          name: 'Products',
          href: '/products',
          id: 'fe__products__view',
        });
      }
      if (
        organization?.permissions?.user_management === 'view' ||
        organization?.permissions?.user_management === 'edit'
      ) {
        addNavigationItem({
          name: 'Users',
          href: '/users',
          id: 'fe__user_management__view',
        });
      }
      // Add GSC Mapping for specific primary organization
      if (profile?.primary_organization_id === 'a4524ca0-beb0-47d6-8a7b-7f08f0f0a7b8') {
        addNavigationItem({
          name: 'GSC Mapping',
          href: '/dh-gsc-mapping',
          id: 'fe__gsc_mapping__view',
        });
      }
    }

    return nav;
  }, [
    forceSetPassword,
    organization?.permissions,
    organization?.available_embeds,
    profile?.primary_organization_id,
    integrations?.available,
  ]);

  // Set the current page as the user's analytics homepage
  const refreshProducts = useCallback(async () => {
    if (!currentOrganization) return;
    try {
      const prods = await getOrganizationProducts(currentOrganization);
      setProducts(prods);
    } catch (err) {
      console.error('❌ Error refreshing products:', err);
    }
  }, [currentOrganization]);

  const setAnalyticsHomepage = useCallback(async (currentMetabaseUrl) => {
    
    if (!currentMetabaseUrl || !user?.id) {
      return;
    }
    
    try {
      setUpdatingHomepage(true);
      
      // Update the user's profile in the public schema
      const { error } = await setMbHomepage(user.id, currentMetabaseUrl);
        
      if (error) {
        console.error('Failed to update analytics homepage:', error);
        return;
      }
      
      // Update the local profile state
      if (profile) {
        profile.analytics_homepage = currentMetabaseUrl;
      }
      
      setCurrentAnalyticsHomepage(currentMetabaseUrl);
    } catch (error) {
      console.error('Error setting analytics homepage:', error);
    } finally {
      setUpdatingHomepage(false);
    }
  }, [user?.id]);

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      currentOrganization,
      organization,
      loading,
      setLoading,
      setCurrentOrganization,
      allowedOrganizations,
      currentOrg,
      allowedNavigation,
      canEditProducts,
      getHgJwt,
      getMbJwt,
      products,
      setProducts,
      refreshProducts,
      integrations,
      setIntegrations,
      refreshProfile: _loadProfile,
      setAnalyticsHomepage,
      updatingHomepage,
      currentMetabaseUrl,
      setCurrentMetabaseUrl,
      currentAnalyticsHomepage,
      isMuffinAdmin,
    }),
    [
      session,
      user,
      profile,
      currentOrganization,
      organization,
      loading,
      allowedOrganizations,
      currentOrg,
      allowedNavigation,
      canEditProducts,
      products,
      refreshProducts,
      integrations,
      setAnalyticsHomepage,
      updatingHomepage,
      currentMetabaseUrl,
      setCurrentMetabaseUrl,
      isMuffinAdmin,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
